// src/utils/utils.ts
import {
  HeadlineFilterMatcher,
  HeadlinePortfolioMatcher,
} from "@/types/Matchers";
import { menuCategories } from "./models/categories";
import { Headline } from "./models/Headline";
import { createWordBoundaryRegex } from "@/utils/text-utils";
import { matchesAnyTicker } from "./tickerMatch";
import { Portfolio } from "@/types/Portfolio";
import {
  extractKeywordsFromPortfolios,
  extractTickersFromPortfolios,
  groupPortfoliosByType,
} from "../firehose-widgets/shared/hooks/searchUtils";
import { CategoryQuery } from "./models/CategoryQuery";

export const formatDate = (dateString: string): string => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(dateString));
};

export const formatTime = (dateString: string): string => {
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(dateString));
};

export const decodeHtmlEntity = (str: string): string => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

/**
 * Mirrors the public site's slug derivation exactly. Order matters: the ASCII
 * space becomes a hyphen FIRST, then every remaining non-word character is
 * deleted (not hyphenated) — so "13:30BST/08:30EDT" collapses to
 * "1330bst0830edt".
 *
 * Do not "tidy" the output. Leading/trailing hyphens, repeated hyphens,
 * accented characters being dropped rather than folded, tabs and non-breaking
 * spaces welding words together — all of that is what the live URLs contain.
 */
export const formatSlug = (val: string): string =>
  val
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "")
    .toLowerCase();

/**
 * Public URL for a headline. Pass the raw stored subject — any trimming,
 * title-casing, entity decoding or smart-quote substitution applied for display
 * will drift the slug away from the one the site derives.
 */
export const headlinePublicUrl = (subject: string): string =>
  `https://www.newsquawk.com/headlines/${formatSlug(subject)}`;

// Searches categoriesMenu
export const getHeadlineCategoryById = (id: number) => {
  for (const category of menuCategories) {
    const item = category.items.find((item) => item.id === id);
    if (item) return item;
  }

  return undefined;
};

export const headlinePortfolioMatcher: HeadlinePortfolioMatcher = (
  item: Headline,
  portfolios: Portfolio[]
) => {
  // Check tickers — curated labels, then parentheses, then bare capitals at
  // three characters and up. See tickerMatch.ts for why the floor exists.
  if (portfolios.length > 0) {
    const tickers = extractTickersFromPortfolios(portfolios);
    if (tickers.length > 0 && matchesAnyTicker(item, tickers)) {
      return true;
    }
  }

  // Check keywords (case-insensitive)
  const keywords = extractKeywordsFromPortfolios(portfolios);

  if (keywords.length > 0) {
    const keywordRegex = createWordBoundaryRegex(keywords, true); // case-insensitive

    if (
      keywordRegex.test(item.subject) ||
      keywordRegex.test(item.content) ||
      keywordRegex.test(item.source)
    ) {
      return true;
    }
  }

  return false;
};

// Helper to check if any keyword from a list matches the headline.
// Uses createWordBoundaryRegex so regex metacharacters (.,+,$...) in a
// watchlist keyword are escaped and word boundaries handle punctuation.
const checkKeywordsMatch = (headline: Headline, keywords: string[]): boolean => {
  if (keywords.length === 0) return false;
  const regex = createWordBoundaryRegex(keywords, true);
  return (
    regex.test(headline.subject) ||
    regex.test(headline.content) ||
    regex.test(headline.source)
  );
};

// Tokenize an expression like `Iran OR "White House" AND Trump` into
// ["Iran", "OR", "White House", "AND", "Trump"]. Handles bare words,
// "double" / 'single' quoted phrases, and treats all whitespace as separator.
// Unterminated quotes swallow the rest of the string.
const tokenizeSearchExpression = (expr: string): string[] => {
  const tokens: string[] = [];
  const n = expr.length;
  let i = 0;
  while (i < n) {
    const ch = expr[i];
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i++;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const end = expr.indexOf(ch, i + 1);
      if (end === -1) {
        tokens.push(expr.slice(i + 1));
        break;
      }
      tokens.push(expr.slice(i + 1, end));
      i = end + 1;
      continue;
    }
    let j = i;
    while (j < n && expr[j] !== " " && expr[j] !== "\t" && expr[j] !== "\n" && expr[j] !== "\r") {
      j++;
    }
    tokens.push(expr.slice(i, j));
    i = j;
  }
  return tokens;
};

// Parse into OR-groups of AND-terms. The whole expression matches if ANY
// group matches (every term in the group is present). AND binds tighter
// than OR; adjacent bare terms without an operator default to OR — matches
// how the backend query is assembled (see endpoints.ts buildSearchTerm).
const parseSearchExpression = (expr: string): string[][] => {
  const tokens = tokenizeSearchExpression(expr);
  const orGroups: string[][] = [];
  let current: string[] = [];
  let andExpected = false;
  for (const token of tokens) {
    const upper = token.toUpperCase();
    if (upper === "OR") {
      if (current.length > 0) orGroups.push(current);
      current = [];
      andExpected = false;
    } else if (upper === "AND") {
      andExpected = true;
    } else if (andExpected) {
      current.push(token);
      andExpected = false;
    } else {
      if (current.length > 0) orGroups.push(current);
      current = [token];
    }
  }
  if (current.length > 0) orGroups.push(current);
  return orGroups;
};

// Evaluate a typed search expression against a headline's subject/content/
// source with word-boundary matching. Replaces the old behavior of treating
// the entire expression as one literal term.
export const matchesSearchExpression = (
  headline: Headline,
  expression: string
): boolean => {
  if (!expression || !expression.trim()) return false;
  const orGroups = parseSearchExpression(expression);
  if (orGroups.length === 0) return false;

  return orGroups.some((group) =>
    group.every((term) => {
      const regex = createWordBoundaryRegex([term], true);
      return (
        regex.test(headline.subject) ||
        regex.test(headline.content) ||
        regex.test(headline.source)
      );
    })
  );
};

// Checks if headline matches the provided filters using the specified logic
// Logic: (typed-keywords [keywordWatchlistOp] keyword-watchlist-1 [keywordWatchlistOp] keyword-watchlist-2...) [keywordOp] ((ticker-watchlists) [filterMode] (tags)) OR (mixed-watchlists)
export const headlineFilterMatcher: HeadlineFilterMatcher = (
  headline: Headline,
  filter: CategoryQuery
): boolean => {
  const portfolios = filter.portfolios || [];
  const groupedPortfolios = groupPortfoliosByType(portfolios);

  // Extract tickers only from ticker-only watchlists (for AND logic with tags)
  const tickerOnlyTickers = extractTickersFromPortfolios(groupedPortfolios.tickerOnly);

  // Keep keyword-only watchlists as separate groups for AND/OR logic
  const keywordWatchlists = groupedPortfolios.keywordOnly.map((p) => ({
    name: p.name,
    keywords: p.keywords || [],
  })).filter((w) => w.keywords.length > 0);

  const tagWatchlistTagIds = (filter.tagsWatchlists || []).flatMap((tw) =>
    tw.tags.map(Number)
  );
  const tagIds = [
    ...filter.categories.map((tag) => Number(tag)),
    ...tagWatchlistTagIds,
  ];

  // Tags a watchlist's own terms resolved to. Kept OUT of tagIds above: that
  // set combines with ticker watchlists under filterMode, which defaults to
  // AND, so folding these in would make every list NARROWER the moment it
  // gained tags. They identify the same companies the list already names, so
  // they can only ever broaden — OR'd into the result below, like mixed.
  const portfolioTagIds = portfolios.flatMap((p) => (p.tags ?? []).map(Number));
  const typedKeywordExpr = filter.searchKeyword?.trim() ?? "";
  const hasTypedKeywords = typedKeywordExpr.length > 0;
  const hasKeywordWatchlists = keywordWatchlists.length > 0;
  const hasAnyKeywords = hasTypedKeywords || hasKeywordWatchlists;
  const hasTags = tagIds.length > 0;
  const hasTickerWatchlists = tickerOnlyTickers.length > 0;
  const hasMixedWatchlists = groupedPortfolios.mixed.length > 0;

  // If no filters provided, match everything
  if (!hasAnyKeywords && !hasTags && !hasTickerWatchlists && !hasMixedWatchlists) {
    return true;
  }

  // Typed keyword field supports OR / AND / "quoted phrases".
  const typedKeywordMatches = hasTypedKeywords && matchesSearchExpression(headline, typedKeywordExpr);

  // Check each keyword watchlist separately
  const watchlistMatchResults = keywordWatchlists.map((watchlist) =>
    checkKeywordsMatch(headline, watchlist.keywords)
  );

  // Combine typed keywords with keyword watchlists based on keywordWatchlistOp
  let keywordMatches = false;
  const keywordWatchlistOp = filter.keywordWatchlistOp || "OR";

  if (keywordWatchlistOp === "AND") {
    // AND mode: typed keyword (if present) AND each watchlist must match
    const allParts: boolean[] = [];
    if (hasTypedKeywords) {
      allParts.push(typedKeywordMatches);
    }
    allParts.push(...watchlistMatchResults);

    // All parts must be true for AND
    keywordMatches = allParts.length > 0 && allParts.every((match) => match);
  } else {
    // OR mode: any typed keyword OR any watchlist keyword matches
    if (hasTypedKeywords && typedKeywordMatches) {
      keywordMatches = true;
    } else if (watchlistMatchResults.some((match) => match)) {
      keywordMatches = true;
    }
  }

  // Check if ANY ticker from ticker-only watchlists matches
  let tickerMatches = false;
  if (hasTickerWatchlists) {
    const tickerRegex = createWordBoundaryRegex(tickerOnlyTickers, false); // case-sensitive
    tickerMatches =
      tickerRegex.test(headline.subject) ||
      tickerRegex.test(headline.content) ||
      tickerRegex.test(headline.source);
  }

  // Check if ANY tag matches
  let tagMatches = false;
  if (hasTags) {
    const tagIdSet = new Set(tagIds);
    tagMatches = headline.tag_id_list.some((tagId) => tagIdSet.has(tagId));
  }

  // A headline carrying a tag one of the list's terms resolved to is a match
  // for that list, however the rest of the filter is composed.
  const portfolioTagMatches =
    portfolioTagIds.length > 0 &&
    headline.tag_id_list.some((tagId) => portfolioTagIds.includes(tagId));

  // Check if mixed watchlists match (OR logic within - tickers OR keywords)
  let mixedMatches = false;
  if (hasMixedWatchlists) {
    mixedMatches = headlinePortfolioMatcher(headline, groupedPortfolios.mixed);
  }

  // Calculate ticker-watchlists/tags match based on filterMode
  let tickersTagsMatch = false;
  if (hasTickerWatchlists || hasTags) {
    // Unset means OR — see FilterMode default.
    if (filter.filterMode !== "AND") {
      tickersTagsMatch = tickerMatches || tagMatches;
    } else {
      // AND mode
      if (hasTickerWatchlists && hasTags) {
        tickersTagsMatch = tickerMatches && tagMatches;
      } else if (hasTickerWatchlists) {
        tickersTagsMatch = tickerMatches;
      } else if (hasTags) {
        tickersTagsMatch = tagMatches;
      }
    }
  }

  // Build the final match result
  // Structure: (keywords [keywordOp] tickersTags) OR mixed

  // If only keywords (no ticker watchlists, no tags)
  if (hasAnyKeywords && !hasTickerWatchlists && !hasTags) {
    return keywordMatches || mixedMatches || portfolioTagMatches;
  }

  // If only ticker watchlists/tags (no keywords)
  if (!hasAnyKeywords && (hasTickerWatchlists || hasTags)) {
    return tickersTagsMatch || mixedMatches || portfolioTagMatches;
  }

  // If only mixed watchlists
  if (!hasAnyKeywords && !hasTickerWatchlists && !hasTags && hasMixedWatchlists) {
    return mixedMatches || portfolioTagMatches;
  }

  // Both keywords and ticker watchlists/tags provided: use keywordOp (default OR to match backend)
  let keywordTickerTagMatch = false;
  const keywordOp = filter.keywordOp || "OR";
  if (keywordOp === "OR") {
    keywordTickerTagMatch = keywordMatches || tickersTagsMatch;
  } else {
    keywordTickerTagMatch = keywordMatches && tickersTagsMatch;
  }

  // Mixed watchlists always OR with everything
  return keywordTickerTagMatch || mixedMatches || portfolioTagMatches;
};
